import { log2 } from "./log2";
import { sendCommandToParentWindow } from "./parentWindowConnection";
import _ from "lodash";
import { getPersistenceData, setPersistenceData } from "./persistenceStorage";
import { matchConditions, buildTrafficSource, OPERATORS, DAYS, computeVisitCount } from "./triggerConditions";
import { pickLangContent, pickTriggerTitle } from "./triggerContent";

const log = log2("TriggerWorker");

// Kart yerlesimiyle ayni kirilim: 768px.
function isMobileViewport() {
    return typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
}

// Ziyaret sayaci: sekmeden bagimsiz, 30 dakikalik hareketsizlik yeni ziyaret sayilir.
function readVisitCount() {
    const r = computeVisitCount(getPersistenceData("supsis-visits"), getPersistenceData("supsis-last-visit"), Date.now());
    if (r.isNewVisit) setPersistenceData("supsis-visits", r.count);
    setPersistenceData("supsis-last-visit", r.lastVisitTs);
    return r.count;
}

let singleton = null;
// SDK verisi singleton kurulmadan once gelebilir; tamponda bekletilir.
let pendingTriggerData = {};
// Referrer parent'tan gelir, singleton kurulmadan once gelebilir.
let pendingReferrer = "";
// Ulke/dil bilgisi socket'ten gelir, singleton kurulmadan once gelebilir.
let pendingLocale = null;
let triggersDisabled = false;

export class Trigger {
    constructor(triggers, lang, offlineMode) {
        log.info("constructor", triggers, lang);
        this.days = DAYS;
        this.operators = OPERATORS;
        this.triggered = {};
        this.triggers = triggers;
        this.triggerData = pendingTriggerData;
        this.offlineMode = offlineMode;
        this.lang = lang || "tr";
        this.tick = parseInt(getPersistenceData("supsis-tick")) || -1;
        this.visitCount = readVisitCount();
        this.pageTick = -1;
        this.title = "";
        this.pageUrl = "";
        this.referrer = pendingReferrer;
        this.locale = pendingLocale || {
            country: "",
            language: (navigator.language || "").split("-")[0].toLowerCase(),
        };

        //workaround for iframe connecting to parent window
        setTimeout(() => {
            if (triggersDisabled) return;
            sendCommandToParentWindow("createTrigger", null);
            this.start();
            this.task = setInterval(this.start.bind(this), 1000 * 10);
        }, 3000);

        singleton = this;
    }

    static create(triggers, lang, offlineMode) {
        if (triggersDisabled) return singleton;
        if (singleton) {
            singleton.triggers = triggers || [];
            if (lang) singleton.lang = lang;
            if (offlineMode !== undefined) singleton.offlineMode = offlineMode;
            // Ayar/trigger güncellenince yeniden tetiklenebilsin
            singleton.triggered = {};
            return singleton;
        }
        return new Trigger(triggers, lang, offlineMode);
    }

    static setLang(lang) {
        if (!!singleton) {
            singleton.lang = lang;
        }
    }

    static setOfflineMode(mode) {
        if (!!singleton) {
            singleton.offlineMode = mode;
        }
    }

    static setURL(url) {
        if (!!singleton) {
            singleton.pageUrl = url;
        }
    }

    static setTitle(title) {
        if (!!singleton) {
            singleton.title = title;
        }
    }

    static setReferrer(referrer) {
        if (singleton) singleton.referrer = referrer || "";
        else pendingReferrer = referrer || "";
    }

    static setLocale(locale) {
        const next = { country: (locale && locale.country) || "", language: (locale && locale.language) || "" };
        if (singleton) singleton.locale = next;
        else pendingLocale = next;
    }

    static setData(data) {
        if (!data || typeof data !== "object") return;
        pendingTriggerData = { ...pendingTriggerData, ...data };
        if (!!singleton) {
            singleton.triggerData = pendingTriggerData;
        }
    }

    start() {
        if (triggersDisabled) return;
        this.pageTick++;
        this.tick++;
        setPersistenceData("supsis-tick", this.tick);
        const now = new Date();
        this.day = now.getDay();
        this.time = now.getHours();
        this.minuteOfDay = now.getHours() * 60 + now.getMinutes();
        log.info("start", this);
        // Kapali tetikleyici sirayi tutmasin; her tick tek tetikleyici islendigi icin acik olani geciktiriyordu.
        let filteredTriggers = this.triggers.filter(
            (trigger) => trigger.enabled !== false && !Object.keys(this.triggered).includes(trigger._id)
        );
        for (const trigger of _.sortBy(filteredTriggers, (x) => x.order)) {
            if (this.isMatch(trigger.conditions)) {
                this.execute(trigger).catch(console.error);
                break;
            }
        }
    }

    isMatch(conditions) {
        log.info("isMatch", conditions);
        return matchConditions(conditions, {
            hour: this.time,
            minuteOfDay: this.minuteOfDay,
            day: this.day,
            pageSeconds: this.pageTick * 10,
            siteSeconds: this.tick * 10,
            pageUrl: this.pageUrl,
            title: this.title,
            offlineMode: this.offlineMode,
            triggerData: this.triggerData,
            deviceType: isMobileViewport() ? "mobile" : "desktop",
            visitCount: this.visitCount,
            trafficSource: buildTrafficSource(this.referrer, this.pageUrl),
            locale: this.locale,
        });
    }

    async execute(trigger) {
        if (triggersDisabled) return;
        log.info("execute trigger", { trigger, lang: this.lang });
        this.triggered[trigger.id] = true;

        const navigatorLanguage = navigator.language.split("-")[0];
        const pick = (list) => pickLangContent(list, this.lang, navigatorLanguage);

        const buttons = (Array.isArray(trigger.buttons) ? trigger.buttons : []).map((b) => ({
            text: pick(b.label),
            action: b.action,
            url: b.url,
            target: b.target || "_blank",
            dismissText: pick(b.dismissMessage),
            style: b.style || "secondary",
        }));

        sendCommandToParentWindow("execTrigger", {
            mode: trigger.mode === "openChat" ? "openChat" : "card",
            title: pickTriggerTitle(trigger, this.lang, navigatorLanguage),
            html: pick(trigger.message),
            image: _.get(trigger, "image.url")
                ? {
                    url: trigger.image.url,
                    link: _.get(trigger, "image.link"),
                    placement: _.get(trigger, "image.placement", "top"),
                    fit: _.get(trigger, "image.fit", "contain"),
                }
                : null,
            buttons,
            position: _.get(trigger, "display.position", "bubble"),
            display: {
                desktop: _.get(trigger, "display.desktop", null),
                tablet: _.get(trigger, "display.tablet", null),
                mobile: _.get(trigger, "display.mobile", null),
            },
            showAvatar: _.get(trigger, "display.showAvatar", false),
            codeBlock: _.get(trigger, "codeBlock.enabled")
                ? {
                    enabled: true,
                    html: _.get(trigger, "codeBlock.html", ""),
                    height: _.get(trigger, "codeBlock.height", 240),
                }
                : null,
            delay: parseInt(trigger.messageDuration) || 0,
            enabled: trigger.enabled,
        });
    }

    static pause() {
        log.info("paused");
        if (!!singleton) {
            clearInterval(singleton.task);
        }
    }

    static resume() {
        if (triggersDisabled) return;
        log.info("resumed");
        if (!!singleton) {
            singleton.task = setInterval(singleton.start.bind(singleton), 1000 * 10);
        }
    }

    static isDisabled() {
        return triggersDisabled;
    }

    static disable() {
        triggersDisabled = true;
        log.info("disabled");
        if (singleton && singleton.task) {
            clearInterval(singleton.task);
            singleton.task = null;
        }
    }
}



// WEBPACK FOOTER //
// ./src/utils/trigger.js